import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, useState} from 'react';

import {Handler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  RelativeDateTimeFormatsEnum,
  RelativeDateTimeFormatter,
} from '../../../../../../../srcCommon/formatters/dateTime/relativeDateTimeFormatter';
import {useConstant} from '../../../../../../../srcCommon/helpers/hookHelpers';
import {buildTeammateDisplayName} from '../../../../../../helpers/modelHelpers';
import {DraftMessageModel, MessageStatusesEnum} from '../../../../../../models/api/messageModel';
import {TeammateModel} from '../../../../../../models/api/teammateModel';
import {OkCancelDialog} from '../../../../../common/dialogs/okCancelDialog';
import {MessageBanner} from './messageBanner';
import {MessageBannerAction} from './messageBannerAction';
import {MessageBannerActionGroup} from './messageBannerActionGroup';
import {MessageBannerSubtitle} from './messageBannerSubtitle';
import {MessageBannerTitle} from './messageBannerTitle';

/*
 * Props.
 */

interface LaterMessageBannerProps {
  message: DraftMessageModel;
  isUserAuthor: boolean;
  author?: TeammateModel;
  onCancelSendClick: Handler;
  isRounded: boolean;
}

/*
 * Constants.
 */

/**
 * How long before we show the "Send later" banner for a message in the "sending" state (in milliseconds).
 *
 * Includes the channel undo period (typically 60 seconds or less) and any other sending delays.
 */
const maxSendDelay = 5 * 60 * 1000;

/*
 * Component.
 */

export const LaterMessageBanner: FC<LaterMessageBannerProps> = (props) => {
  const maxSentAt = useMaxSentAt();

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);

  const onCancelClick = () => setIsCancelConfirmationOpen(true);
  const onCancelConfirmationRequestClose = () => setIsCancelConfirmationOpen(false);

  // Don't show this banner for a "sending" message unless it's taking way more time than expected.
  if (props.message.status === MessageStatusesEnum.DRAFT_SENDING && props.message.sentAt <= maxSentAt) {
    return null;
  }

  return (
    <>
      {renderBanner(props, onCancelClick)}
      {isCancelConfirmationOpen && renderCancelConfirmationDialog(props, onCancelConfirmationRequestClose)}
    </>
  );
};

/*
 * Helpers.
 */

function useMaxSentAt(): number {
  const now = useConstant(() => Date.now());
  return now + maxSendDelay;
}

function renderBanner({isRounded, message}: LaterMessageBannerProps, onCancelClick: Handler): ReactNode {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <MessageBanner
          color={theme.palette.orange.shade40}
          iconName="snoozeFilledSmall"
          isRounded={isRounded}
        >
          <MessageBannerTitle>
            <Trans id="sendlater-title">Send Later</Trans>
          </MessageBannerTitle>
          <MessageBannerSubtitle>{renderRemainingTimeBeforeSent(message.sentAt)}</MessageBannerSubtitle>
          <MessageBannerActionGroup>
            <MessageBannerAction onClick={onCancelClick}>
              <Trans id="sendlater-cancel">Cancel</Trans>
            </MessageBannerAction>
          </MessageBannerActionGroup>
        </MessageBanner>
      )}
    </UserThemeConsumer>
  );
}

function renderRemainingTimeBeforeSent(sentAt: number): ReactNode {
  const minutesFromNow = Math.floor((sentAt - Date.now()) / 60000);

  if (minutesFromNow <= 0) {
    return (
      <MessageBannerSubtitle>
        <Trans id="sendlater-subtitle-now">This message will be sent now</Trans>
      </MessageBannerSubtitle>
    );
  }

  const relativeSentNow = (
    <RelativeDateTimeFormatter value={sentAt} format={RelativeDateTimeFormatsEnum.LONG} />
  );
  return <Trans id="sendlater-subtitle-later">This message will be sent in {relativeSentNow}</Trans>;
}

function renderCancelConfirmationDialog(props: LaterMessageBannerProps, onRequestClose: Handler): ReactNode {
  return (
    <OkCancelDialog
      title={<Trans id="sendlater-cancel-title">Cancel message</Trans>}
      okButtonLabel={<Trans id="sendlater-cancel-ok">Revert</Trans>}
      onOkClick={props.onCancelSendClick}
      onRequestClose={onRequestClose}
    >
      {renderCancelConfirmationContent(props.isUserAuthor, props.author)}
    </OkCancelDialog>
  );
}

function renderCancelConfirmationContent(
  isUserAuthor: boolean,
  author: TeammateModel | undefined,
): ReactNode {
  if (!author || isUserAuthor) {
    return (
      <Trans id="sendlater-cancel-user">
        This message has not been sent yet.
        <br />
        Would you like to cancel and revert it back to a draft?
      </Trans>
    );
  }

  return (
    <Trans id="sendlater-cancel-otheruser">
      This message from {buildTeammateDisplayName(author)} has not been sent yet.
      <br />
      Would you like to cancel and revert it back to a draft?
    </Trans>
  );
}
